import { Controller } from "stimulus"

// #1706: het opstelscherm van een mailing. Houdt de tellingen rechts bij
// terwijl de filters veranderen (zonder op te slaan), zoekt leden om
// handmatig toe te voegen of uit te sluiten, toont Bekijk ontvangers en vraagt
// om bevestiging vóór verzenden of inplannen.
export default class extends Controller {
  static targets = ["modus", "filters", "telling", "zoekveld", "zoekresultaten", "toegevoegd", "uitgesloten",
    "bevestiging", "ontvangers", "ontvangerslijst", "onderwerp", "afzendernaam", "antwoordadres", "moment"]
  static values = { tellingUrl: String, zoekUrl: String, direct: String, toevoegen: String, uitsluiten: String,
    verwijder: String }

  connect() {
    this.zoekveldTargets.forEach((veld) => veld.addEventListener("keydown", this.geenEnter))
  }

  disconnect() {
    clearTimeout(this.tellingTimer)
    clearTimeout(this.zoekTimer)
  }

  geenEnter(event) {
    if (event.key === "Enter") event.preventDefault()
  }

  wijzigModus() {
    const alle = this.modusTargets.find((radio) => radio.checked)?.value === "1"
    this.filtersTarget.hidden = alle
  }

  ververs() {
    clearTimeout(this.tellingTimer)
    this.tellingTimer = setTimeout(() => this.haalTelling(), 250)
  }

  async haalTelling() {
    const html = await this.post(this.tellingUrlValue, this.formulierdata())
    if (html !== null) this.tellingTarget.innerHTML = html
  }

  async toonOntvangers() {
    const data = this.formulierdata()
    data.append("lijst", "1")
    this.ontvangerslijstTarget.innerHTML = ""
    if (!this.ontvangersTarget.open) this.ontvangersTarget.showModal()
    const html = await this.post(this.tellingUrlValue, data)
    if (html !== null) this.ontvangerslijstTarget.innerHTML = html
  }

  sluitUitVanuitLijst(event) {
    const knop = event.currentTarget
    this.voegToe(knop.dataset.id, knop.dataset.naam, "uitgesloten")
    this.toonOntvangers()
  }

  zoek() {
    clearTimeout(this.zoekTimer)
    this.zoekTimer = setTimeout(() => this.haalLeden(), 250)
  }

  async haalLeden() {
    const term = this.zoekveldTarget.value.trim()
    if (term.length < 2) {
      this.zoekresultatenTarget.innerHTML = ""
      return
    }
    const url = `${this.zoekUrlValue}${this.zoekUrlValue.includes("?") ? "&" : "?"}q=${encodeURIComponent(term)}`
    const antwoord = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
    if (!antwoord.ok) return
    const leden = await antwoord.json()
    this.zoekresultatenTarget.innerHTML = ""
    leden.forEach((lid) => {
      const regel = document.createElement("li")
      const naam = document.createElement("span")
      naam.textContent = lid.email ? `${lid.naam} · ${lid.email}` : lid.naam
      const knoppen = document.createElement("span")
      knoppen.style.display = "inline-flex"
      knoppen.style.gap = "6px"
      knoppen.append(this.knop(this.toevoegenValue, () => this.voegToe(lid.id, lid.naam, "toegevoegd")))
      knoppen.append(this.knop(this.uitsluitenValue, () => this.voegToe(lid.id, lid.naam, "uitgesloten")))
      regel.append(naam, knoppen)
      this.zoekresultatenTarget.append(regel)
    })
  }

  knop(tekst, actie) {
    const knop = document.createElement("button")
    knop.type = "button"
    knop.className = "ml-knop"
    knop.textContent = tekst
    knop.addEventListener("click", actie)
    return knop
  }

  // Zet een lid in de lijst toegevoegd of uitgesloten (en haalt hem uit de andere)
  voegToe(id, naam, soort) {
    id = String(id)
    const doel = soort === "toegevoegd" ? this.toegevoegdTarget : this.uitgeslotenTarget
    const ander = soort === "toegevoegd" ? this.uitgeslotenTarget : this.toegevoegdTarget
    ander.querySelector(`[data-id="${CSS.escape(id)}"]`)?.remove()
    if (!doel.querySelector(`[data-id="${CSS.escape(id)}"]`)) {
      const persoon = document.createElement("span")
      persoon.className = soort === "toegevoegd" ? "ml-persoon" : "ml-persoon is-uit"
      persoon.dataset.id = id
      persoon.append(document.createTextNode(naam || id))
      const veld = document.createElement("input")
      veld.type = "hidden"
      veld.name = `mailing[${soort === "toegevoegd" ? "toegevoegd_ids" : "uitgesloten_ids"}][]`
      veld.value = id
      const weg = document.createElement("button")
      weg.type = "button"
      weg.textContent = "×"
      weg.setAttribute("aria-label", this.verwijderValue)
      weg.dataset.action = "mailing-opstellen#verwijderPersoon"
      persoon.append(veld, weg)
      doel.append(persoon)
    }
    this.zoekresultatenTarget.innerHTML = ""
    this.zoekveldTarget.value = ""
    this.ververs()
  }

  verwijderPersoon(event) {
    event.currentTarget.closest(".ml-persoon")?.remove()
    this.ververs()
  }

  // Verzenden of inplannen: eerst een venster met wat er gaat gebeuren
  bevestig(event) {
    if (this.bevestigdeKnop === event.currentTarget) {
      this.bevestigdeKnop = null
      return
    }
    event.preventDefault()
    this.knopInAfwachting = event.currentTarget
    const venster = this.bevestigingTarget
    const telling = this.tellingTarget
    const zet = (veld, waarde) => { venster.querySelector(`[data-veld="${veld}"]`).textContent = waarde || "–" }
    zet("onderwerp", this.onderwerpTarget.value)
    zet("doelgroep", telling.querySelector("[data-doelgroep-omschrijving]")?.textContent?.trim())
    zet("aantal", telling.querySelector("[data-aantal='te_versturen']")?.textContent?.trim())
    const antwoord = this.antwoordadresTarget.value ? ` (${this.antwoordadresTarget.value})` : ""
    zet("afzender", `${this.afzendernaamTarget.value}${antwoord}`)
    if (this.knopInAfwachting.dataset.bevestig === "gepland" && this.hasMomentTarget) {
      const moment = this.momentTarget.value ? new Date(this.momentTarget.value) : null
      zet("moment", moment ? moment.toLocaleString(document.documentElement.lang || "nl", { dateStyle: "full", timeStyle: "short" }) : "")
    } else {
      zet("moment", this.directValue)
    }
    venster.showModal()
  }

  bevestigd() {
    const knop = this.knopInAfwachting
    this.bevestigingTarget.close()
    if (!knop) return
    this.bevestigdeKnop = knop
    this.element.requestSubmit(knop)
  }

  sluit(event) {
    event.currentTarget.closest("dialog")?.close()
  }

  formulierdata() {
    const data = new FormData(this.element)
    data.delete("_method")
    return data
  }

  async post(url, data) {
    try {
      const antwoord = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", Accept: "text/html" },
        body: data,
        credentials: "same-origin"
      })
      return antwoord.ok ? await antwoord.text() : null
    } catch (fout) {
      return null
    }
  }
}
